import { suggestDestinationForCsvColumn, CSV_IMPORT_SERVICE_SPEED_FIELDS } from "./projectCsvImportFields";
import {
  canonicalizeDepartmentList,
  canonicalizeDepartmentName,
  parseMultiFromCsvCell,
  serviceSpeedLabel,
} from "./commercialProjectSchema";
import { MATERIALS_JSON_FIELD_ID, formatMaterialsImportPreviewValue } from "./materialsJsonImport";
import { PERMITS_JSON_FIELD_ID, formatPermitsImportPreviewValue } from "./permitsJsonImport";

export const DEPARTMENTS_FIELD_ID = "project_summary.departments";

function columnsMappedTo(columnToDest, fieldId) {
  return Object.entries(columnToDest || {})
    .filter(([, dest]) => dest === fieldId)
    .map(([col]) => col);
}

/** True when a departments-aliased CSV column was explicitly set to "Do not import". */
export function departmentsMappingSkipped(columnToDest) {
  for (const [header, dest] of Object.entries(columnToDest || {})) {
    if (dest !== "") continue;
    if (suggestDestinationForCsvColumn(header) === DEPARTMENTS_FIELD_ID) return true;
  }
  return false;
}

function overflowParts(csvRow) {
  const raw = csvRow?._csv_overflow;
  if (raw == null || raw === "") return [];
  return String(raw)
    .split("\u001f")
    .map((s) => s.trim())
    .filter(Boolean);
}

/**
 * Collect departments when a comma-separated list was split into extra CSV columns
 * (unquoted) or spread across multiple mapped columns.
 */
export function collectDepartmentsFromCsvRow(csvRow, columnToDest, destToColumn) {
  const parts = [];

  for (const col of columnsMappedTo(columnToDest, DEPARTMENTS_FIELD_ID)) {
    parts.push(...parseMultiFromCsvCell(csvRow[col]));
  }

  const primaryCol = destToColumn?.[DEPARTMENTS_FIELD_ID];
  if (primaryCol && !columnsMappedTo(columnToDest, DEPARTMENTS_FIELD_ID).includes(primaryCol)) {
    parts.push(...parseMultiFromCsvCell(csvRow[primaryCol]));
  }

  parts.push(...overflowParts(csvRow));

  for (const [header, val] of Object.entries(csvRow || {})) {
    if (header === "_csv_overflow") continue;
    const mappedDest = columnToDest?.[header];
    // Explicit "Do not import" (empty dest) must not be harvested.
    if (mappedDest === "") continue;
    if (mappedDest && mappedDest !== DEPARTMENTS_FIELD_ID) continue;

    const fromHeader = canonicalizeDepartmentName(header);
    if (fromHeader) {
      const v = String(val ?? "").trim();
      if (!v || canonicalizeDepartmentName(v) === fromHeader) {
        parts.push(fromHeader);
      }
      continue;
    }
    if (mappedDest === DEPARTMENTS_FIELD_ID) continue;
    if (suggestDestinationForCsvColumn(header) === DEPARTMENTS_FIELD_ID) continue;
    const fromVal = canonicalizeDepartmentName(val);
    if (fromVal) parts.push(fromVal);
  }

  return canonicalizeDepartmentList(parts);
}

/** Preview table columns: every mapped destination, even when empty in sample rows. */
export function importPreviewPayloadKeys(previewRows, mappedDestinationIds) {
  const keys = new Set(mappedDestinationIds || []);
  for (const pr of previewRows || []) {
    for (const k of Object.keys(pr?.payload || {})) {
      if (k !== "_source") keys.add(k);
    }
  }
  return [...keys].sort((a, b) => {
    if (a === "project_summary.work_order") return -1;
    if (b === "project_summary.work_order") return 1;
    return a.localeCompare(b);
  });
}

export function formatImportPreviewValue(value, fieldId) {
  if (fieldId === PERMITS_JSON_FIELD_ID) return formatPermitsImportPreviewValue(value);
  if (fieldId === MATERIALS_JSON_FIELD_ID) return formatMaterialsImportPreviewValue(value);
  if (CSV_IMPORT_SERVICE_SPEED_FIELDS.has(fieldId)) {
    const label = serviceSpeedLabel(value);
    return label || "—";
  }
  if (value == null) return "—";
  if (Array.isArray(value)) return value.length ? value.join(", ") : "—";
  if (typeof value === "string" && value.includes("\u001f")) {
    return value.split("\u001f").join(", ");
  }
  if (typeof value === "object") return JSON.stringify(value);
  return String(value);
}
